import React, { useState, useEffect, useMemo } from "react";
import api from "@/api";
import CropModal from "./CropModal";
import { updateUserSettings } from "../settings";
import DataSearchFilters from "./search/DataSearchFilters";
import useDataSearch from "../hooks/useDataSearch";
import MasterDataRowActions, { ArchivedBadge, ShowArchivedToggle } from "./masterdata/ArchiveControls";
import { visibleRows } from "@/utils/archive";

// Searchable columns, as shown in the table
const SEARCH_FIELDS = [
  { key: "name", label: "Name", type: "text", value: (c) => c.name },
  { key: "description", label: "Description", type: "text", value: (c) => c.description },
];

export default function CropTable() {
  const [crops, setCrops] = useState([]);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const stored = sessionStorage.getItem("cropsPerPage");
    if (stored === "all") return "all";
    const num = stored ? Number(stored) : 15;
    return [15, 25, 50].includes(num) ? num : 15;
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editCrop, setEditCrop] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  function fetchCrops() {
    setLoading(true);
    setError(null);
    api
      .post("/masterdata/crops")
      .then((res) => setCrops(res.data))
      .catch((e) => setError("Failed to load crops"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    fetchCrops();
  }, []);

  // Persist page size
  useEffect(() => {
    sessionStorage.setItem("cropsPerPage", String(pageSize));
  }, [pageSize]);

  const shown = useMemo(() => visibleRows(crops, showArchived), [crops, showArchived]);
  const search = useDataSearch(shown, SEARCH_FIELDS, { onChange: () => setPage(1) });

  const filtered = useMemo(() => {
    let data = search.filtered;
    if (sortKey) {
      data = [...data].sort((a, b) => {
        let v1 = a[sortKey],
          v2 = b[sortKey];
        if (typeof v1 === "string") v1 = v1.toLowerCase();
        if (typeof v2 === "string") v2 = v2.toLowerCase();
        if (v1 < v2) return sortDir === "asc" ? -1 : 1;
        if (v1 > v2) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [search.filtered, sortKey, sortDir]);

  const paginated = useMemo(() => {
    if (pageSize === "all") return filtered;
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const totalPages =
    pageSize === "all" ? 1 : Math.max(1, Math.ceil(filtered.length / pageSize));

  // Ensure page within bounds
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  function handleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function handleEdit(crop) {
    setEditCrop(crop);
    setModalOpen(true);
  }

  return (
    <div className="w-full">
      <DataSearchFilters {...search.panelProps} />
      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <ShowArchivedToggle checked={showArchived} onChange={setShowArchived} />
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 px-3 py-2 rounded flex items-center gap-1 border border-gray-300 dark:border-gray-600"
            onClick={fetchCrops}
            title="Refresh"
            disabled={loading}
          >
            <svg
              className={loading ? "animate-spin w-4 h-4" : "w-4 h-4"}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582M20 20v-5h-.581M5.582 9A7.974 7.974 0 0112 4c2.21 0 4.21.896 5.657 2.343M18.418 15A7.974 7.974 0 0112 20a7.974 7.974 0 01-5.657-2.343"
              />
            </svg>
            <span className="hidden sm:inline text-sm font-medium">
              Refresh
            </span>
          </button>
          <button
            className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold shadow"
            onClick={() => {
              setEditCrop(null);
              setModalOpen(true);
            }}
          >
            + Add Crop
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-center text-gray-400 dark:text-gray-500 py-10 text-sm">
          Loading crops...
        </div>
      ) : error ? (
        <div className="text-center text-red-600 dark:text-red-400 py-10 text-sm">
          {error}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow">
            <table className="min-w-full align-middle text-sm whitespace-nowrap border-separate border-spacing-0">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700/50">
                  <th className="bg-gray-100/75 px-3 py-3 text-center font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">
                    Photo
                  </th>
                  <th
                    className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50 cursor-pointer select-none"
                    onClick={() => handleSort("name")}
                  >
                    Name{" "}
                    {sortKey === "name" && (
                      <span className="ml-1 text-xs">
                        {sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                  <th
                    className="bg-gray-100/75 px-3 py-3 text-left font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50 cursor-pointer select-none"
                    onClick={() => handleSort("description")}
                  >
                    Description{" "}
                    {sortKey === "description" && (
                      <span className="ml-1 text-xs">
                        {sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                  <th className="bg-gray-100/75 px-3 py-3 text-right font-semibold text-gray-900 dark:bg-gray-700/25 dark:text-gray-50">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-6 text-center text-gray-400">
                      No crops found.
                    </td>
                  </tr>
                ) : (
                  paginated.map((c) => (
                    <tr
                      key={c.id}
                      className="border-b border-gray-100 dark:border-gray-700/50"
                    >
                      <td className="px-3 py-2 text-center">
                        <img
                          src={c.photoUrl || "/profile-header.png"}
                          alt="Crop Avatar"
                          className="inline-block size-10 rounded-full"
                        />
                      </td>
                      <td className="px-3 py-2 font-medium">{c.name}<ArchivedBadge row={c} /></td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                        {c.description}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm leading-5 font-semibold text-gray-800 hover:border-gray-300 hover:text-gray-900 hover:shadow-xs focus:ring-3 focus:ring-gray-300/25 active:border-gray-200 active:shadow-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:text-gray-200 dark:focus:ring-gray-600/40 dark:active:border-gray-700"
                            onClick={() => handleEdit(c)}
                          >
                            Edit
                          </button>
                          <MasterDataRowActions type="crops" row={c} name={c.name} noun="crop" onChanged={fetchCrops} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-4 w-full">
            {pageSize !== "all" && filtered.length > pageSize && (
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 rounded border bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="px-2 text-sm font-semibold">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="px-3 py-1 rounded border bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-50"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            )}
            <label className="inline-flex items-center gap-2 text-sm font-medium ml-auto">
              <span>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "all") {
                    setPageSize("all");
                    setPage(1);
                    fetchCrops();
                    updateUserSettings({ crops_per_page: "all" });
                  } else {
                    const num = Number(val);
                    setPageSize(num);
                    setPage(1);
                    fetchCrops();
                    updateUserSettings({ crops_per_page: num });
                  }
                }}
                className="form-select rounded border-gray-300 px-2 py-1 text-sm dark:border-gray-700 dark:bg-gray-800"
              >
                {[15, 25, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
                <option value="all">All</option>
              </select>
            </label>
          </div>
        </>
      )}
      <CropModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        crop={editCrop}
        onSaved={() => {
          setModalOpen(false);
          fetchCrops();
        }}
      />
    </div>
  );
}
